import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { TUserState } from './user.type';
import { oneClubApi } from '~/api/oneClub';
import { TOneClubGetUserTokenCode } from '~/api/oneClub/oneclub.type';
import { onePassApi } from '~/api/onePass';
import { TAPIResponseStatus } from '~/api/baseFetch/common.type';
import { useCommon } from '~/utils/hook/useCommon';
import { oneBoxV3Api } from '~/api/oneBoxV3';
import { resourceEduCode } from '~/api/oneBoxV3/oneBoxV3.type';
import { Authority } from '~/types/enum';
import { updateEduCode, updateUserRole } from '~/tracking';
import { clearSentryUser, setSentryUser } from '~/utils/sentry';

const initialState: TUserState = {
  ownInfo: {
    disabled: false,
    createdAt: '',
    updatedAt: '',
    _id: '',
    identity: '',
    authority: Authority.CLIENT,
    name: '',
    username: '',
    schoolID: '',
    schoolCounty: '',
    schoolDistrict: '',
    schoolName: '',
    eduCode: '',
    academic: '',
    schoolLevel: '',
    recommendSetting: null,
  },
  showLoginDialog: false,
  showAdminReloginDialog: false,
};

// 只要有 token 就會有 ownInfo
// 但只有家長和學生以外的權限才能 isValidLogin

// !token => 未登入
// token && ownInfo && !isValidLogin => 不符合權限的身份
// token && ownInfo && isValidLogin => 真正可以使用的身份

// eslint-disable-next-line react-hooks/rules-of-hooks
const { setToken, deleteToken, clearOfflineDisplayName } = useCommon();

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    logout: state => {
      state.ownInfo = initialState.ownInfo;
      deleteToken();
      // 保留裝置「曾登入過」資格，只清除上一位使用者的 Header 名稱。
      clearOfflineDisplayName();
      updateUserRole('');
      updateEduCode('未登入');
      clearSentryUser();
    },
    setShowLoginDialog: (state, action: PayloadAction<boolean>) => {
      state.showLoginDialog = action.payload;
    },
    setShowAdminReloginDialog: (state, action: PayloadAction<boolean>) => {
      state.showAdminReloginDialog = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addMatcher(
      oneClubApi.endpoints.getUserToken.matchFulfilled,
      (_state, action) => {
        const token = action.payload;
        if (token.jwt) {
          setToken(token);
        }
      },
    );
    builder.addMatcher(
      oneBoxV3Api.endpoints.getOwnInfo.matchFulfilled,
      (state, action) => {
        state.ownInfo = action.payload.data || initialState.ownInfo;
        updateUserRole(state.ownInfo.authority);
        const rawEdu = state.ownInfo.eduCode as keyof typeof resourceEduCode;
        updateEduCode(resourceEduCode[rawEdu] ?? '未驗證');
        setSentryUser({
          id: state.ownInfo._id,
          username: state.ownInfo.username,
          authority: state.ownInfo.authority,
        });
      },
    );
    builder.addMatcher(
      onePassApi.endpoints.getQrcodeToken.matchFulfilled,
      (_state, action) => {
        if (action.payload.status === TAPIResponseStatus.success) {
          const jwt = action.payload.data;
          if (jwt) {
            setToken({ code: TOneClubGetUserTokenCode.success, jwt });
          }
        }
      },
    );
  },
});

export const { logout, setShowLoginDialog, setShowAdminReloginDialog } =
  userSlice.actions;

export default userSlice.reducer;
